import { z } from 'zod'

/**
 * Validation schemas for questionnaire editor
 */

// Question schema
export const questionSchema = z.object({
  id: z.string()
    .min(1, 'L\'ID est requis')
    .regex(/^[a-zA-Z0-9_-]+$/, 'L\'ID ne peut contenir que des lettres, chiffres, tirets et underscores'),
  text: z.string()
    .min(3, 'Le texte doit contenir au moins 3 caractères')
    .max(500, 'Le texte ne peut pas dépasser 500 caractères'),
  weight: z.number()
    .min(1, 'Le poids doit être au moins 1')
    .max(10, 'Le poids ne peut pas dépasser 10')
})

// Theme schema
export const themeSchema = z.object({
  id: z.string()
    .min(1, 'L\'ID est requis')
    .regex(/^[a-zA-Z0-9_-]+$/, 'L\'ID ne peut contenir que des lettres, chiffres, tirets et underscores'),
  title: z.string()
    .min(3, 'Le titre doit contenir au moins 3 caractères')
    .max(100, 'Le titre ne peut pas dépasser 100 caractères'),
  icon: z.string()
    .min(1, 'L\'icône est requise'),
  description: z.string()
    .min(10, 'La description doit contenir au moins 10 caractères')
    .max(500, 'La description ne peut pas dépasser 500 caractères'),
  questions: z.array(questionSchema)
    .min(1, 'Le thème doit contenir au moins 1 question')
    .max(20, 'Le thème ne peut pas contenir plus de 20 questions')
})

// Questionnaire schema
export const questionnaireSchema = z.object({
  title: z.string()
    .min(5, 'Le titre doit contenir au moins 5 caractères')
    .max(200, 'Le titre ne peut pas dépasser 200 caractères'),
  description: z.string()
    .min(10, 'La description doit contenir au moins 10 caractères')
    .max(1000, 'La description ne peut pas dépasser 1000 caractères'),
  themes: z.array(themeSchema)
    .min(1, 'Le questionnaire doit contenir au moins 1 thème')
    .max(50, 'Le questionnaire ne peut pas contenir plus de 50 thèmes')
})

// Metadata schema (version is auto-generated by backend)
export const metadataSchema = z.object({
  changelog: z.string()
    .min(10, 'Le changelog doit contenir au moins 10 caractères')
    .max(2000, 'Le changelog ne peut pas dépasser 2000 caractères')
})

// Complete submission schema
export const questionnaireSubmissionSchema = z.object({
  questionnaire: questionnaireSchema,
  metadata: metadataSchema
})

/**
 * Validate unique IDs in themes and questions
 * @param {Object} questionnaire - Questionnaire data
 * @returns {Object} - { valid: boolean, errors: string[] }
 */
export const validateUniqueIds = (questionnaire) => {
  const errors = []
  
  // Check if questionnaire and themes exist
  if (!questionnaire || !questionnaire.themes || !Array.isArray(questionnaire.themes)) {
    errors.push('Le questionnaire doit contenir un tableau de thèmes')
    return {
      valid: false,
      errors
    }
  }
  
  // Check theme IDs are unique
  const themeIds = questionnaire.themes.map(t => t.id)
  const duplicateThemeIds = themeIds.filter((id, index) => themeIds.indexOf(id) !== index)
  
  if (duplicateThemeIds.length > 0) {
    errors.push(`IDs de thèmes dupliqués: ${[...new Set(duplicateThemeIds)].join(', ')}`)
  }
  
  // Check question IDs are unique across all themes
  const allQuestionIds = questionnaire.themes.flatMap(theme => 
    theme.questions && Array.isArray(theme.questions) ? theme.questions.map(q => q.id) : []
  )
  const duplicateQuestionIds = allQuestionIds.filter((id, index) => 
    allQuestionIds.indexOf(id) !== index
  )
  
  if (duplicateQuestionIds.length > 0) {
    errors.push(`IDs de questions dupliqués: ${[...new Set(duplicateQuestionIds)].join(', ')}`)
  }
  
  return {
    valid: errors.length === 0,
    errors
  }
}

/**
 * Calculate questionnaire statistics
 * @param {Object} questionnaire - Questionnaire data
 * @returns {Object} - Statistics
 */
export const calculateQuestionnaireStats = (questionnaire) => {
  // Check if questionnaire and themes exist
  if (!questionnaire || !questionnaire.themes || !Array.isArray(questionnaire.themes)) {
    return {
      totalThemes: 0,
      totalQuestions: 0,
      totalMaxScore: 0,
      avgQuestionsPerTheme: 0
    }
  }
  
  const totalThemes = questionnaire.themes.length
  const totalQuestions = questionnaire.themes.reduce(
    (sum, theme) => sum + (theme.questions && Array.isArray(theme.questions) ? theme.questions.length : 0), 
    0
  )
  const totalMaxScore = questionnaire.themes.reduce(
    (sum, theme) => {
      if (!theme.questions || !Array.isArray(theme.questions)) return sum
      return sum + theme.questions.reduce((qSum, q) => qSum + (q.weight * 10), 0)
    },
    0
  )
  
  return {
    totalThemes,
    totalQuestions,
    totalMaxScore,
    avgQuestionsPerTheme: totalThemes > 0 ? (totalQuestions / totalThemes).toFixed(1) : 0
  }
}

/**
 * Generate a unique ID suggestion
 * @param {string} prefix - Prefix for the ID
 * @param {Array} existingIds - Array of existing IDs
 * @returns {string} - Unique ID
 */
export const generateUniqueId = (prefix, existingIds = []) => {
  let counter = 1
  let id = `${prefix}_${counter}`
  
  while (existingIds.includes(id)) {
    counter++
    id = `${prefix}_${counter}`
  }
  
  return id
}

export default {
  questionSchema,
  themeSchema,
  questionnaireSchema,
  metadataSchema,
  questionnaireSubmissionSchema,
  validateUniqueIds,
  calculateQuestionnaireStats,
  generateUniqueId
}

